import {useMemo} from 'react';
import {CellValue, DurationUnit, ReportRow} from '../data/reportRowModel';
import {fieldByKey, ReportTaxonomy} from '../contracts/report-taxonomy.types';

/** A grouping token as the report definition and the catalog spell it.
 *
 * Deliberately a bare string rather than a union of the task grain's eight. The catalog publishes a
 * `groupingValues` list per grain and they do not overlap - a project-status report groups by stage
 * and RAG, which no union of task tokens can name - so a closed union here would either exclude
 * every other grain's tokens or have to be extended for each new one. What a token is ALLOWED to be
 * is answered per grain by `groupingTokensFor`, not by this type. */
export type GroupingToken = string;

/** The task grain's eight tokens and the cell each one groups by. Two of them - `client` and
 * `project` - name something a project-status row also has, under a different path, which is why
 * the map below is resolved per grain rather than shared.
 *
 * `client` reads the project's client rather than a row-level one, because that is where a task's
 * client actually lives: ProjectResolver resolves the client sub-entity onto the project node and
 * the catalog publishes it as `task.project.client.name`. It used to name `task.client.name`, a
 * path with no catalog key and no producer behind it, so grouping by this published token read an
 * absent cell on every row and collapsed the whole report into one bucket. */
const TASK_GROUPING_CELL_PATH: Record<string, string> = {
	project: 'task.project.info',
	phase: 'task.phase.name',
	client: 'task.project.client.name',
	role: 'task.role.info',
	spint: 'task.sprint.name',
	task: 'task.name',
	assignee: 'task.assignees.firstName',
	deliverable: 'task.deliverable.name',
};

/** A grouping token whose cell path a grain does not spell out follows the catalog's own key: the
 * token names a field of the row, and every grain but task keys that field as `<root>.<token>` or
 * `<root>.<token>.name`. Which of the two is decided by the catalog, so a token is resolved against
 * the fields it actually publishes rather than by guessing a suffix. */
function groupingCellPathFrom(taxonomy: ReportTaxonomy | null | undefined, root: string, token: string): string {
	const nested = `${root}.${token}.name`;
	if (taxonomy && fieldByKey(taxonomy, nested)) {
		return nested;
	}
	const bare = `${root}.${token}`;
	if (taxonomy && fieldByKey(taxonomy, bare)) {
		return bare;
	}
	return nested;
}

/** Resolves the token -> cell-path map for a report: the task grain's own eight, or the grain's
 * catalog fields for anything else. A grouping token with no path groups every row into one
 * bucket, so this is the difference between grouping and appearing to group. */
export function groupingCellPaths(root: string, taxonomy?: ReportTaxonomy | null): Record<string, string> {
	if (root === 'task') {
		return TASK_GROUPING_CELL_PATH;
	}
	const paths: Record<string, string> = {};
	for (const token of taxonomy?.groupingValues ?? []) {
		paths[token] = groupingCellPathFrom(taxonomy, root, token);
	}
	return paths;
}

/** The cell path holding a row's budget type on this grain, or undefined when the grain publishes
 * none.
 *
 * Resolved from the catalog rather than assumed, for the same reason `groupingCellPaths` is: which
 * cell holds a per-row fact is a per-grain question, and a grain that publishes no budget type must
 * get no budget-type rule rather than a rule reading an absent cell on every row - which would make
 * every row the same kind and quietly disable the check. */
export function budgetTypePathFor(root: string, taxonomy?: ReportTaxonomy | null): string | undefined {
	const key = `${root}.budgetType`;
	return taxonomy && fieldByKey(taxonomy, key) ? key : undefined;
}

/** The tokens a report of this grain may be grouped by, as the catalog publishes them. An empty
 * list (no taxonomy yet) means nothing is offered rather than the wrong grain's tokens. */
export function groupingTokensFor(root: string, taxonomy?: ReportTaxonomy | null): GroupingToken[] {
	if (root === 'task') {
		return Object.keys(TASK_GROUPING_CELL_PATH);
	}
	return [...(taxonomy?.groupingValues ?? [])];
}

/** Why a total that COULD have been computed is being refused.
 *
 * A withheld total is not the same thing as no total. A percentage has no total by nature and its
 * cell is simply empty; a duration total the group's own rows made meaningless is refused, and the
 * reader is told so. Those are two different blanks a reader needs to be able to tell apart, so they
 * are encoded as present-with-a-reason and absent-from-the-record rather than as two flavours of
 * empty value.
 *
 * `truncatedRows` is different in kind from the other two: it describes what happened to the FETCH
 * (only part of the report's rows ever arrived), not something the contributing rows disagree on.
 * Nothing in this module ever assigns it - `totalsFromLeaves` below only ever produces `mixedUnits` or
 * `mixedBudgetTypes` - because a group header's total is honest over whatever rows it was actually
 * given. It exists here so a caller that knows the fetch was capped (which this module has no way to
 * know) can carry that fact through the same `GroupTotal` shape onto the whole-table footer. */
export type WithheldReason = 'mixedUnits' | 'mixedBudgetTypes' | 'truncatedRows';

export type GroupTotal = {kind: 'total'; minutes: number; unit: DurationUnit} | {kind: 'withheld'; reason: WithheldReason};

export interface GroupHeaderRow extends ReportRow {
	isGroupHeader: true;
	groupLabel: string;
	groupCount: number;
	/** Per-column duration totals. A path ABSENT from this record has no total by nature (a
	 * percentage, a name); a path present with `kind: 'withheld'` had one and it is being refused. */
	groupTotals: Record<string, GroupTotal>;
}

/** Fallback 'No <label>' text per task token when the group-defining cell is absent. 'spint'
 * (legacy spelling for 'sprint') gets the corrected 'No sprint' label. */
const NO_GROUP_LABEL: Record<string, string> = {
	project: 'No project',
	phase: 'No phase',
	client: 'No client',
	role: 'No role',
	spint: 'No sprint',
	task: 'No task',
	assignee: 'No assignee',
	deliverable: 'No deliverable',
};

/** The bucket a row with no value for the grouping token falls into. Named from the catalog for a
 * token the task list does not cover, so a project-status report says 'No stage' rather than
 * 'No undefined'. */
function noGroupLabel(token: GroupingToken, cellPath: string, taxonomy?: ReportTaxonomy | null): string {
	const known = NO_GROUP_LABEL[token];
	if (known) {
		return known;
	}
	const label = taxonomy ? fieldByKey(taxonomy, cellPath)?.label : undefined;
	return `No ${(label ?? token).toLowerCase()}`;
}

/** Extracts the display label(s) a row contributes to grouping by `token`. Most cell kinds
 * contribute a single label; the 'persons' cell (used for assignee) contributes one label per
 * person so a multi-assignee row is duplicated across each assignee's group. An empty array
 * means the row falls back to the token's 'No <x>' bucket. */
function labelsForToken(row: ReportRow, token: GroupingToken, cellPaths: Record<string, string>): string[] {
	const cell: CellValue | undefined = row.cells[cellPaths[token]];
	if (cell === undefined) {
		return [];
	}
	switch (cell.kind) {
		case 'persons':
			return cell.persons.map(p => p.name);
		case 'tag':
			// A repeated sub-entity, exactly like 'persons': a row carrying two labels belongs in both
			// of their groups, so it contributes one label per tag and is duplicated across them.
			// Without this case a tag cell fell to the default below and every row landed in the
			// 'No labels' bucket, which reads as a grouping that ran and found nothing.
			return cell.tags.map(tag => tag.name).filter(name => name !== '');
		case 'text':
			return cell.value ? [cell.value] : [];
		case 'project':
			return [cell.name];
		case 'status':
			return [cell.label];
		default:
			return [];
	}
}

/** What grouping needs beyond the rows: which cell each token reads, and the catalog that names a
 * token's empty bucket. */
export interface GroupingOptions {
	cellPaths: Record<string, string>;
	taxonomy?: ReportTaxonomy | null;
	/** The cell path holding a row's budget type, when the grain publishes one. Absent means this
	 * grain has no budget-type rule, which is what the task grain wants - resolved by the caller for
	 * the same reason `cellPaths` is, so this module stays grain-agnostic. */
	budgetTypePath?: string;
}

/** What TOTALLING needs, which is strictly less than what grouping needs: a sum reads no grouping
 * cell path and no catalog, only which cell holds a row's budget type. Kept as its own type so the
 * table-total entry points cannot claim to depend on a taxonomy they never read - a memo keyed on
 * one recomputes every total whenever the definition's identity changes, for no reason. */
export type TotalsOptions = Pick<GroupingOptions, 'budgetTypePath'>;

interface Bucket {
	label: string;
	rows: ReportRow[];
}

/** Buckets top-level `rows` by the labels they contribute for `token`, preserving first-seen
 * label order. A row contributing multiple labels (multi-assignee) is pushed into each of its
 * buckets; a row contributing none goes into the token's 'No <x>' bucket. */
function bucketRows(rows: ReportRow[], token: GroupingToken, options: GroupingOptions): Bucket[] {
	const buckets = new Map<string, Bucket>();
	const fallback = noGroupLabel(token, options.cellPaths[token], options.taxonomy);

	for (const row of rows) {
		const labels = labelsForToken(row, token, options.cellPaths);
		const targetLabels = labels.length > 0 ? labels : [fallback];
		for (const label of targetLabels) {
			let bucket = buckets.get(label);
			if (!bucket) {
				bucket = {label, rows: []};
				buckets.set(label, bucket);
			}
			bucket.rows.push(row);
		}
	}

	return Array.from(buckets.values());
}

/** The kind a row with no budget type counts as. Deliberately not spellable as a wire value: the
 * catalog publishes `FIXED_PRICE`, `RETAINER` and friends today and is free to publish more, and a
 * plain `'NONE'` here would compare EQUAL to a real value spelled the same way - silently making a
 * statusless row compatible with it and re-enabling the mixed sum this rule exists to refuse. */
const NO_BUDGET_TYPE = ' no-budget-type';

/** The budget type a row contributes to a total.
 *
 * A row that names none counts as its OWN kind rather than being skipped, which is load-bearing:
 * treating "no budget type" as compatible with everything lets through exactly the mixed sum this
 * rule exists to refuse. */
function budgetTypeOf(row: ReportRow, budgetTypePath: string): string {
	const cell = row.cells[budgetTypePath];
	if (cell === undefined || cell.kind !== 'text' || cell.value === '') {
		return NO_BUDGET_TYPE;
	}
	return cell.value;
}

/** Sums duration cells at `path` across `rows` (each row's own cell only, not recursing into
 * children). Returns null when no row has a duration cell at that path - the caller then omits the
 * path entirely, which is how "no total by nature" is spelled.
 *
 * A sum is refused, not approximated, when the rows that CONTRIBUTED to it disagree on something
 * the sum depends on. Only contributing rows are considered, which is what makes this per-column
 * rather than per-group: a retainer column in a group holding one fixed-price project takes no
 * contribution from that project, so its sum mixes nothing and is shown. */
function sumDurationsAtPath(rows: ReportRow[], path: string, options: TotalsOptions): GroupTotal | null {
	let minutes = 0;
	// Doubles as the "did any row contribute?" flag: every contributing row assigns it, and a
	// `DurationUnit` is never itself null, so a null here means the loop found no duration cell.
	let unit: DurationUnit | null = null;
	let sawMixedUnits = false;
	let budgetType: string | null = null;
	let sawMixedBudgetTypes = false;

	for (const row of rows) {
		const cell = row.cells[path];
		if (cell === undefined || cell.kind !== 'duration') {
			continue;
		}
		if (unit === null) {
			unit = cell.unit;
		} else if (unit !== cell.unit) {
			sawMixedUnits = true;
		}
		if (options.budgetTypePath !== undefined) {
			const rowBudgetType = budgetTypeOf(row, options.budgetTypePath);
			if (budgetType === null) {
				budgetType = rowBudgetType;
			} else if (budgetType !== rowBudgetType) {
				sawMixedBudgetTypes = true;
			}
		}
		minutes += cell.minutes;
	}

	if (unit === null) {
		return null;
	}
	// Mixed units win when both apply: hours and points cannot be added at all, whereas same-unit
	// hours across budget types are addable but meaningless. Naming the harder incompatibility is
	// the more useful message. On the project-status grain units cannot mix - every project duration
	// is hours - but this must not depend on that.
	if (sawMixedUnits) {
		return {kind: 'withheld', reason: 'mixedUnits'};
	}
	if (sawMixedBudgetTypes) {
		return {kind: 'withheld', reason: 'mixedBudgetTypes'};
	}
	return {kind: 'total', minutes, unit};
}

/** Returns the row's stable identity for de-dup purposes: the id up to (excluding) the first
 * `::`, which strips the `::<label>` suffix `withDecollidedId` adds to multi-assignee duplicates.
 * A task duplicated across several assignee sub-groups shares this base id across all its copies. */
function baseRowId(row: ReportRow): string {
	const suffixIndex = row.id.indexOf('::');
	return suffixIndex === -1 ? row.id : row.id.slice(0, suffixIndex);
}

/** Recursively collects the descendant leaf DATA rows under `rows` for group-totalling purposes:
 * recurses through `GroupHeaderRow.children` (synthetic nesting levels) but treats any real
 * `ReportRow` as a leaf and does not descend into its own subtask/deliverable `children` — matches
 * the pre-existing (unreviewed-as-wrong) leaf-level behaviour of only totalling each data row's own
 * cells, not its hierarchy's. De-duplicates by base id (see `baseRowId`) so a multi-assignee task
 * duplicated across several assignee sub-groups is counted once toward an ancestor total. */
function collectDescendantDataRows(rows: ReportRow[]): ReportRow[] {
	const seen = new Set<string>();
	const result: ReportRow[] = [];

	function visit(row: ReportRow): void {
		if ((row as GroupHeaderRow).isGroupHeader === true) {
			for (const child of row.children) {
				visit(child);
			}
			return;
		}
		const id = baseRowId(row);
		if (seen.has(id)) {
			return;
		}
		seen.add(id);
		result.push(row);
	}

	for (const row of rows) {
		visit(row);
	}
	return result;
}

/** Computes per-column duration totals from an ALREADY-collected leaf data set (see
 * `collectDescendantDataRows`) - never a group's immediate children, which for any non-leaf nesting
 * level are themselves synthetic `GroupHeaderRow`s with empty `cells` and would make every ancestor
 * level's totals silently empty. Takes the leaves rather than collecting them so a caller that also
 * needs the leaf COUNT (see `buildGroupHeaderRow`) walks the subtree once instead of twice. Totals
 * across every field path that appears as a duration cell on at least one contributing leaf row; a
 * path with no contribution is omitted rather than nulled. */
function totalsFromLeaves(leaves: ReportRow[], options: TotalsOptions): Record<string, GroupTotal> {
	const paths = new Set<string>();
	for (const row of leaves) {
		for (const [path, cell] of Object.entries(row.cells)) {
			if (cell.kind === 'duration') {
				paths.add(path);
			}
		}
	}

	const totals: Record<string, GroupTotal> = {};
	for (const path of paths) {
		const total = sumDurationsAtPath(leaves, path, options);
		if (total !== null) {
			totals[path] = total;
		}
	}
	return totals;
}

/** Builds a group header pseudo-row. Its id embeds the full token/label path so nested grouping
 * levels (and repeated labels across sibling branches, e.g. the same assignee under two
 * different projects) never collide. It satisfies `ReportRow` so the table renders its `children`
 * through the same subrow mechanism as real rows. */
function buildGroupHeaderRow(
	idPath: string,
	token: GroupingToken,
	label: string,
	children: ReportRow[],
	options: GroupingOptions
): GroupHeaderRow {
	// Collected once and reused for both the count and the totals: the walk de-duplicates by base id
	// and allocates a Set per call, and it runs at every nesting level of every group.
	const leaves = collectDescendantDataRows(children);
	return {
		id: `group:${token}:${idPath}`,
		type: 'task',
		parentId: null,
		// A synthetic grouping header is not a task, so there is nothing to open.
		companyTaskId: null,
		children,
		matchedFilter: true,
		favorite: false,
		cells: {},
		isGroupHeader: true,
		groupLabel: label,
		// Descendant leaf DATA rows, not immediate `children`: at any non-leaf grouping level the
		// immediate children are themselves synthetic group headers, so `children.length` would
		// report the sub-group count instead of the number of tasks under this group.
		groupCount: leaves.length,
		groupTotals: totalsFromLeaves(leaves, options),
	};
}

/** De-collides a row's id for the group branch it has been duplicated into. Only rows that
 * contribute more than one label for the current token (multi-assignee tasks) are actually
 * duplicated across branches; giving every duplicate a `::<groupLabel>` suffix keeps React keys
 * and TanStack's internal row ids unique even though the underlying row object is shared across
 * groups. Children keep their original ids since they are not independently duplicated. */
function withDecollidedId(row: ReportRow, groupLabel: string, duplicated: boolean): ReportRow {
	if (!duplicated) {
		return row;
	}
	return {...row, id: `${row.id}::${groupLabel}`};
}

/** Replaces each top-level deliverable row with its children. A deliverable row is a structural
 * container report-service promotes from the tasks beneath it (the real hierarchy is project →
 * deliverable → task → subtask); it carries no task cells of its own, so bucketing it directly
 * drops every task beneath it into the token's 'No <x>' bucket. Only the top level is unwrapped —
 * `promoteDeliverables` produces deliverable rows only there, and grouping never descended into
 * `children` anyway. The unwrapped tasks keep a `parentId` pointing at the row that no longer
 * exists; nothing in the table reads `parentId` (nesting is rendered from `children`), and the
 * pre-existing multi-assignee duplication already breaks that link the same way. Returns `rows`
 * unchanged when there is nothing to unwrap.
 *
 * Exported so tests that need to predict a group's contents or count mirror this transform by
 * calling it, rather than re-deriving it and drifting from what grouping actually does. */
export function unwrapDeliverableRows(rows: ReportRow[]): ReportRow[] {
	if (!rows.some(row => row.type === 'deliverable')) {
		return rows;
	}
	return rows.flatMap(row => (row.type === 'deliverable' ? row.children : [row]));
}

/** Recursively groups `rows` by the ordered `grouping` tokens (max 4 levels). Only top-level
 * rows are distributed into groups at each level; each row's own `children` (subtasks/deliverables)
 * stay attached beneath it, untouched, inside whichever group it lands in. */
function groupRowsRecursive(
	rows: ReportRow[],
	grouping: GroupingToken[],
	idPathPrefix: string,
	options: GroupingOptions
): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}

	const [token, ...restTokens] = grouping;
	const buckets = bucketRows(rows, token, options);

	return buckets.map(bucket => {
		const branchRows = bucket.rows.map(row => {
			const rowDuplicated = labelsForToken(row, token, options.cellPaths).length > 1;
			return withDecollidedId(row, bucket.label, rowDuplicated);
		});
		const idPath = idPathPrefix ? `${idPathPrefix}/${bucket.label}` : bucket.label;
		const nestedChildren = groupRowsRecursive(branchRows, restTokens, idPath, options);
		return buildGroupHeaderRow(idPath, token, bucket.label, nestedChildren, options);
	});
}

/** Pure grouping transform: groups only top-level `rows` (never descends into `children`) by the
 * ordered `grouping` tokens, nesting group header pseudo-rows up to 4 levels deep. Duplicates
 * multi-assignee rows across each contributing assignee's group. Returns `rows` unchanged (same
 * reference) when `grouping` is empty. */
export function groupRows(rows: ReportRow[], grouping: GroupingToken[], options?: Partial<GroupingOptions>): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}
	const resolved: GroupingOptions = {
		cellPaths: options?.cellPaths ?? TASK_GROUPING_CELL_PATH,
		taxonomy: options?.taxonomy,
		budgetTypePath: options?.budgetTypePath,
	};
	// A token the grain does not publish has no cell to read, so every row would land in one bucket
	// under a heading naming a field the report has not got. Dropping it groups by what remains,
	// which is what the reader asked for minus the part that could never have worked.
	const known = grouping.filter(token => resolved.cellPaths[token] !== undefined);
	// definition contract caps grouping at 4 levels regardless of how many tokens the caller passes.
	const cappedGrouping = known.slice(0, 4);
	if (cappedGrouping.length === 0) {
		return rows;
	}
	return groupRowsRecursive(unwrapDeliverableRows(rows), cappedGrouping, '', resolved);
}

/** Thin memoization wrapper around `groupRows` for use in table components. */
export function useClientGrouping(
	rows: ReportRow[],
	grouping: GroupingToken[],
	options?: Partial<GroupingOptions>
): ReportRow[] {
	const cellPaths = options?.cellPaths;
	const taxonomy = options?.taxonomy;
	const budgetTypePath = options?.budgetTypePath;
	return useMemo(
		() => groupRows(rows, grouping, {cellPaths, taxonomy, budgetTypePath}),
		[rows, grouping, cellPaths, taxonomy, budgetTypePath]
	);
}

/** Per-column duration totals over EVERY row, for the whole-table total row.
 *
 * Runs the same `totalsFromLeaves` the group headers run, over the same de-duplicated descendant leaf
 * set - which is why a sub-group's totals, its parent's, and the table's agree by construction
 * rather than by a separate reconciliation. Accepts either grouped or ungrouped rows:
 * `collectDescendantDataRows` walks through group headers to the leaves either way. */
export function computeTableTotals(rows: ReportRow[], options?: TotalsOptions): Record<string, GroupTotal> {
	return totalsFromLeaves(collectDescendantDataRows(unwrapDeliverableRows(rows)), {
		budgetTypePath: options?.budgetTypePath,
	});
}

/** Thin memoization wrapper around `computeTableTotals`, matching `useClientGrouping`. Keyed on the
 * one option a total actually reads, so a new `cellPaths`/`taxonomy` identity - which the grouping
 * options object gets on every definition change - does not re-total every row for nothing. */
export function useTableTotals(rows: ReportRow[], options?: TotalsOptions): Record<string, GroupTotal> {
	const budgetTypePath = options?.budgetTypePath;
	return useMemo(() => computeTableTotals(rows, {budgetTypePath}), [rows, budgetTypePath]);
}
